import { useEffect, useState } from "react";
import { api } from "../../api/apiClient";
import type { HandTypedNearby } from "../../api/types";
import { calendarDayText, dateText, money, percentText } from "../../i18n/intl";

/**
 * G-121 S4 (OD-145): a purchase enters the app once — by its paper or typed by
 * hand. So beside a factura, the purchases from the same supplier typed by
 * hand in the six weeks before it: if one of them is what this factura bills,
 * it is already counted, and this paper should be "Items only". A hint; it
 * never blocks. Managers see their own shop.
 */
export function HandTypedPurchasesHint({ invoiceId }: { invoiceId: string }) {
  const [hint, setHint] = useState<HandTypedNearby | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    api
      .getHandTypedNearby(invoiceId)
      .then((h) => {
        if (!cancelled) setHint(h);
      })
      .catch((err: unknown) => {
        if (!cancelled) setError(err instanceof Error ? err.message : "Could not load purchases typed by hand");
      });
    return () => {
      cancelled = true;
    };
  }, [invoiceId]);

  if (error) return <div className="alert error">{error}</div>;
  if (!hint || !hint.applicable) return null;

  return (
    <div className="inv-hand-typed">
      <strong>Already typed by hand?</strong>
      <p className="muted inv-hand-typed-note">
        Purchases from this supplier typed by hand between {calendarDayText(hint.fromDate)} and{" "}
        {calendarDayText(hint.toDate)}. If this factura bills one of them, that purchase is already in the
        app — change this paper to &ldquo;Items only&rdquo; so it is not counted twice.
      </p>
      {hint.rows.length === 0 ? (
        <p className="muted inv-hand-typed-note">None.</p>
      ) : (
        <div className="table-scroll" tabIndex={0} role="region" aria-label="Purchases typed by hand">
          <table className="table inv-hand-typed-table">
            <thead>
              <tr>
                <th scope="col">Day</th>
                <th scope="col">Shop</th>
                <th scope="col">Item</th>
                <th scope="col">Before IVA</th>
                <th scope="col">IVA</th>
                <th scope="col">With IVA</th>
              </tr>
            </thead>
            <tbody>
              {hint.rows.map((r) => (
                <tr key={r.id}>
                  <td>
                    {dateText(new Date(r.at))}
                    {r.source === "MISSING_DELIVERY_LINE" ? <div className="muted">nothing arrived</div> : null}
                  </td>
                  <td>{r.location.name}</td>
                  <td>{r.item.name}</td>
                  <td>{money(r.netEur)}</td>
                  <td>
                    {money(r.ivaEur)} <span className="muted">({percentText(r.ivaRatePercent)} %)</span>
                  </td>
                  <td>{money(r.grossEur)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
